import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  Box,
  Breadcrumbs,
  Container,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { Link as RouterLink, useLocation } from "react-router-dom";

import AppBarComponent from "../componentes/appBar";
import DrawerComponent from "../componentes/Drawer";
import Footer from "../componentes/Footer";
import { CondicaoContext } from "../contexto";
import { usePreferencias } from "../contextos/PreferenciasContext";

import { ajustarTamanhoFonte } from "../estilos/global";

const PAGINAS_COM_RODAPE_AUTOMATICO = {
  admin: [
    "/admin/visualizar/sre",
    "/admin/visualizar/cidade",
    "/admin/catalogo-ensino",
  ],
  superintendent: [
    "/admin/processo",
    "/admin/cadastro-documento",
    "/modelos",
    "/admin/usuarios",
  ],
  analyst: ["/modelos"],
  user: ["/modelos", "/home/perfil", "/home/processos"],
};

export default function RenderPage({ children }) {
  const areaPrincipalRef = useRef(null);

  const [rolagem, setRolagem] = useState({
    pagina: "",
    posicao: 0,
  });

  const { menuInicial, chaveUsuario } = usePreferencias();
  const [menuOpen, setMenuOpen] = useState(menuInicial === "expandido");
  const [menuExpandido, setMenuExpandido] = useState(menuInicial === "expandido");
  const darkMode = useTheme().palette.mode === "dark";
  useEffect(() => {
    setMenuExpandido(menuInicial === "expandido");
    setMenuOpen(menuInicial === "expandido");
  }, [chaveUsuario, menuInicial]);

  const { condicao, role, loading } = useContext(CondicaoContext);
  const { pathname } = useLocation();
  const telaPequena = useMediaQuery("(max-width:899px)");

  const rodapeAutomatico =
    !condicao &&
    !telaPequena &&
    Boolean(
      PAGINAS_COM_RODAPE_AUTOMATICO[role]?.includes(pathname) ||
        (role === "user" && pathname.startsWith("/detalhes-processo/")) ||
        (role === "analyst" && pathname.startsWith("/processo-detalhado/")),
    );

  const registrarPosicao = useCallback(
    (posicao) => {
      setRolagem((atual) =>
        atual.pagina === pathname && atual.posicao === posicao
          ? atual
          : { pagina: pathname, posicao },
      );
    },
    [pathname],
  );

  const acompanharRolagem = useCallback(
    (evento) => {
      const alvo = evento.target;

      if (
        alvo instanceof HTMLElement &&
        alvo.hasAttribute("data-rolagem-conteudo") &&
        evento.currentTarget.contains(alvo)
      ) {
        registrarPosicao(alvo.scrollTop);
      }
    },
    [registrarPosicao],
  );

  useLayoutEffect(() => {
    if (!rodapeAutomatico || !areaPrincipalRef.current) return;

    const area = areaPrincipalRef.current;
    let listaAnterior = null;

    registrarPosicao(0);

    // A tabela pode ser criada ou removida ao carregar ou filtrar a lista.
    const sincronizarLista = () => {
      const lista = area.querySelector("[data-rolagem-conteudo]");

      if (lista === listaAnterior) return;

      listaAnterior = lista;
      registrarPosicao(lista?.scrollTop ?? 0);
    };

    sincronizarLista();

    const observador = new MutationObserver(sincronizarLista);
    observador.observe(area, {
      // A aba ativa muda a área de rolagem sem desmontar as listas.
      ...(pathname === "/home/perfil" && {
        attributes: true,
        attributeFilter: ["data-rolagem-conteudo"],
      }),
      childList: true,
      subtree: true,
    });

    return () => observador.disconnect();
  }, [rodapeAutomatico, registrarPosicao, pathname]);

  const posicaoRolagem =
    rodapeAutomatico && rolagem.pagina === pathname ? rolagem.posicao : 0;

  const visualGlobal = !condicao;
  const layoutAdmin = !condicao && role === "admin";
  const layoutSre = !condicao && role === "superintendent";
  const layoutAnalista = !condicao && role === "analyst";
  const layoutEscola = !condicao && role === "user";

  const layoutAcompanhaMenu =
    layoutAdmin || layoutSre || layoutAnalista || layoutEscola;


  // Padrão compartilhado de altura da janela.
  // Preserva o comportamento responsivo existente do ADMIN.
  const layoutAlturaTela = visualGlobal && (layoutAdmin || !telaPequena);

  const paginasAdmin = {
    "/admin/painel": "Início",
    "/admin/visualizar/sre": "SREs",
    "/admin/visualizar/cidade": "Cidades",
    "/admin/catalogo-ensino": "Catálogo de Ensino",
    "/admin/usuarios": "Usuários",
    "/admin/perfil": "Meu perfil",
  };

  const paginaAtualAdmin = paginasAdmin[pathname];

  const toggleDrawer = (estadoForcado) => {
    if (visualGlobal && !telaPequena) {
      if (typeof estadoForcado === "boolean") {
        setMenuExpandido(estadoForcado);
      } else {
        setMenuExpandido((atual) => !atual);
      }
      return;
    }

    setMenuOpen((atual) => !atual);
  };

  const fecharMenuDesktop = () => {
    // O interruptor ligado mantém o menu aberto durante o uso da página.
    // Ações explícitas no botão do cabeçalho continuam disponíveis.
    if (menuInicial !== "expandido") setMenuExpandido(false);
  };

  const drawer = !loading && (
    <DrawerComponent
      menuOpen={menuOpen}
      toggleDrawer={toggleDrawer}
      visualNovo={visualGlobal}
      expandido={menuExpandido}
      onCloseDesktop={fecharMenuDesktop}
      acompanharMenu={layoutAcompanhaMenu}
    />
  );

  return (
    <>


      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",

          ...(layoutAlturaTela && {
            height: { xs: "auto", md: "100dvh" },
            overflow: { xs: "visible", md: "hidden" },
          }),
        }}
      >
        <AppBarComponent
          toggleDrawer={toggleDrawer}

          menuAberto={visualGlobal && !telaPequena ? menuExpandido : menuOpen}
          darkMode={darkMode}
          visualNovo={visualGlobal}
          exibirUsuarioAdmin={layoutAdmin}
        />

        {visualGlobal ? (
          <Box
            sx={{
              display: "flex",
              flex: "1 1 auto",
              minHeight: 0,
              alignItems: "stretch",
            }}
          >
            {drawer}

            <Box
              component="main"
              ref={areaPrincipalRef}
              onScrollCapture={rodapeAutomatico ? acompanharRolagem : undefined}
              sx={{
                flexGrow: 1,
                minWidth: 0,

                ...(layoutAlturaTela && {
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,

                  ...(!rodapeAutomatico && {
                    overflowY: {
                      xs: "visible",
                      md: "auto",
                    },
                  }),
                }),

                ...(rodapeAutomatico && {
                  overflow: "hidden",

                  "& [data-pagina-rolavel]": {
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 0",
                    minHeight: 0,
                    overflow: "hidden",
                    "& > *": { flexShrink: 0 },
                  },

                  "& [data-rolagem-conteudo]": {
                    flex: "1 1 0",
                    minHeight: 0,
                    overflow: "auto",
                    overflowAnchor: "none",

                    "& thead th": {
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      backgroundColor: "var(--sear-superficie-azul, #edf6fe)",
                    },
                  },
                }),

                bgcolor: darkMode ? "background.default" : "var(--sear-superficie, #ffffff)",
                px: {
                  xs: 2,
                  md: layoutAcompanhaMenu ? 3 : 4,
                },
                pt: { xs: 1.5, md: 2 },
                pb: { xs: 1, md: 1 },
              }}
            >
              <Container
                maxWidth={layoutAcompanhaMenu ? false : "lg"}
                disableGutters
                sx={
                  layoutAlturaTela
                    ? {
                        display: "flex",
                        flexDirection: "column",
                        flex: "1 1 auto",
                        minHeight: 0,

                        ...(rodapeAutomatico && {
                          overflow: "hidden",
                        }),

                        ...(rodapeAutomatico &&
                          pathname === "/admin/cadastro-documento" && {
                            "& > :first-child": {
                              display: "flex",
                              flexDirection: "column",
                              flex: "1 1 auto",
                              minHeight: 0,
                              overflow: "hidden",
                            },
                          }),

                        ...(layoutAnalista &&
                          pathname === "/analise" && {
                            "& > :first-child": {
                              display: "flex",
                              flexDirection: "column",
                              flex: "1 1 auto",
                              minHeight: 0,
                            },
                          }),
                      }
                    : undefined
                }
              >
                {layoutAdmin && paginaAtualAdmin && (
                  <Breadcrumbs
                    aria-label="Caminho de navegação"
                    separator="›"
                    sx={{
                      mb: 1,
                      ...(layoutAlturaTela && {
                        flexShrink: 0,
                      }),
                      fontSize: ajustarTamanhoFonte("0.9rem"),
                      color: "var(--sear-texto-secundario, #6b7892)",
                    }}
                  >
                    {pathname !== "/admin/painel" && (
                      <Link
                        component={RouterLink}
                        to="/admin/painel"
                        underline="hover"
                        sx={{
                          color: "inherit",
                          fontSize: "inherit",
                        }}
                      >
                        Início
                      </Link>
                    )}

                    <Typography
                      sx={{
                        fontSize: "inherit",
                        color: "var(--sear-azul, #0767d8)",
                        fontWeight: 700,
                      }}
                    >
                      {paginaAtualAdmin}
                    </Typography>
                  </Breadcrumbs>
                )}

                {children}
              </Container>
            </Box>
          </Box>
        ) : (
          <>
            {drawer}

            <Container
              component="main"
              maxWidth="lg"
              sx={{
                mt: 2,
                mb: 2,
                flexGrow: 1,
              }}
            >
              {children}
            </Container>
          </>
        )}

        <Footer
          visualNovo={visualGlobal}
          recolherAoRolar={rodapeAutomatico}
          posicaoRolagem={posicaoRolagem}
        />
      </Box>
    </>
  );
}
